"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MAX_FILTER_ITEMS = void 0;
exports.filterItems = filterItems;
exports.encodeFilterItem = encodeFilterItem;
exports.decodeFilterItem = decodeFilterItem;
exports.filterParam = filterParam;
exports.commaListSchema = commaListSchema;
const zod_1 = require("zod");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 3 Nov 2026 · a choice filter on the wire is a COMMA LIST — one rule.        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The product owner's ruling (`decisions.md` R-62): every list filter that picks from options picks several — OR within
 * one filter, AND across filters. The wire keeps the parameter it had: `?status=active,paused`. A
 * single value is a one-item list, so a saved view or bookmark from before still parses. A
 * repeated parameter (`?status=a&status=b`) reads the same. `apps/api/src/list-filter.ts` is the
 * API's reading for the hand-written query readers; this is the zod one, for a query schema.
 *
 * ⚠️ **A malformed item is DROPPED, not refused** — never a 500 from a cast, never the whole request
 * turned away for one stale id. None surviving is no filter at all, which is what one malformed
 * value meant before.
 *
 * ⚠️ **More than {@link MAX_FILTER_ITEMS} items is `validation_failed`.**
 *
 * ⚠️⚠️ **A FREE-TEXT ITEM IS ESCAPED: `%` → `%25`, then `,` → `%2C`** ({@link encodeFilterItem}).
 * An id or an enum value never holds either, so for them the rule costs nothing; an industry, a
 * Jira component or a metafield option is the workspace's own words and may (*Média, szórakoztatás*).
 * Only those two characters, so an item without them — every value a saved view held before — is
 * its own encoding and keeps working.
 */
exports.MAX_FILTER_ITEMS = 100;
/** The raw items of a comma list, repeated or not. */
function filterItems(value) {
    const raw = Array.isArray(value) ? value : [value];
    return raw
        .filter((one) => typeof one === 'string')
        .flatMap((one) => one.split(','))
        .map((one) => one.trim())
        .filter((one) => one !== '');
}
/** One free-text item, made safe to join with a comma. */
function encodeFilterItem(value) {
    return value.replaceAll('%', '%25').replaceAll(',', '%2C');
}
/** The inverse of {@link encodeFilterItem}. Harmless on an id or enum value. */
function decodeFilterItem(value) {
    return value.replace(/%2C/giu, ',').replace(/%25/gu, '%');
}
/** The wire form of a list filter — the one place a screen joins its chosen values. */
function filterParam(values) {
    return values.join(',');
}
/**
 * A query-schema field holding a comma list of `item`. Output: the surviving items, or `undefined`.
 */
function commaListSchema(item) {
    return zod_1.z.preprocess((value) => {
        if (value === undefined || value === null)
            return undefined;
        const items = filterItems(value);
        /* Over the cap is refused below by `.max`, so it must reach the array unfiltered. */
        if (items.length > exports.MAX_FILTER_ITEMS)
            return items;
        const kept = [...new Set(items.map(decodeFilterItem))].filter((one) => item.safeParse(one).success);
        return kept.length === 0 ? undefined : kept;
    }, zod_1.z.array(item).max(exports.MAX_FILTER_ITEMS).optional());
}
//# sourceMappingURL=list-filter.js.map